/**
 * Scenario helpers for the package generator: Phase 1 presets, saved scenarios
 * (this browser only - nothing is stored on the server), and run-to-run
 * comparison by content hash.
 */
import type { PreviewDocument, PreviewRun, Scenario, ScenarioProduct } from './packagePreviewApi';

export function blankProduct(code: string): ScenarioProduct {
  return { code, ownerIndexes: [], orderChecks: false, overdraft: 'none', odLimit: 500, beneficiaries: [] };
}

export function blankScenario(): Scenario {
  return {
    ownershipType: 'Individual',
    stampOwnershipOnCart: false,
    branchCode: '',
    people: [{ role: 'primary', minor: false }],
    products: [],
    versionSource: 'active',
    compilePdfs: true,
  };
}

export interface Preset {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  /** Product codes the preset needs; hidden when the catalog lacks one. */
  readonly needs: ReadonlyArray<string>;
  readonly build: () => Scenario;
}

// Product codes are the ones the Phase 1 review set used (scripts/pilot/ginger-scenarios.py):
// FREE, SAVPER, MMKT, SAVMIN, STUDEN_MINOR. A preset disappears if its product is not in
// the live catalog, so nothing here can invent a product.
const withProducts = (base: Partial<Scenario>, products: ScenarioProduct[]): Scenario => ({
  ...blankScenario(),
  ...base,
  products,
});

export const PRESETS: ReadonlyArray<Preset> = [
  {
    id: 'ind-free-od',
    label: 'Individual - Free Checking, overdraft coverage elected',
    detail: '1 adult owner; Reg E opt-in at the $500 limit; no checks, no beneficiaries.',
    needs: ['FREE'],
    build: () => withProducts({}, [{ ...blankProduct('FREE'), overdraft: 'optIn', odLimit: 500 }]),
  },
  {
    id: 'ind-free-checks-pod',
    label: 'Individual - Free Checking, checks ordered, 1 adult beneficiary',
    detail: 'Check order on the account; one primary payable-on-death beneficiary.',
    needs: ['FREE'],
    build: () => withProducts({}, [{ ...blankProduct('FREE'), orderChecks: true, beneficiaries: [{ role: 'primary', minor: false }] }]),
  },
  {
    id: 'joint-free-sav-checks',
    label: 'Joint (2 owners) - Free Checking with checks + Personal Savings',
    detail: 'Both owners on both accounts; checks ordered on the checking account.',
    needs: ['FREE', 'SAVPER'],
    build: () => withProducts(
      { ownershipType: 'JointTenant', people: [{ role: 'primary', minor: false }, { role: 'joint', minor: false }] },
      [{ ...blankProduct('FREE'), orderChecks: true }, blankProduct('SAVPER')],
    ),
  },
  {
    id: 'joint-sav-minor-benef',
    label: 'Joint (2 owners) - Personal Savings, 1 minor beneficiary',
    detail: 'Minor beneficiary with a custodian; picks the custodian POD designation.',
    needs: ['SAVPER'],
    build: () => withProducts(
      { ownershipType: 'JointTenant', people: [{ role: 'primary', minor: false }, { role: 'joint', minor: false }] },
      [{ ...blankProduct('SAVPER'), beneficiaries: [{ role: 'primary', minor: true }] }],
    ),
  },
  {
    id: 'joint-4-free-checks',
    label: 'Joint (4 owners) - Free Checking, checks ordered',
    detail: 'Every owner signs the signature card when checks are ordered.',
    needs: ['FREE'],
    build: () => withProducts(
      { ownershipType: 'JointTenant', people: [{ role: 'primary', minor: false }, { role: 'joint', minor: false }, { role: 'joint', minor: false }, { role: 'joint', minor: false }] },
      [{ ...blankProduct('FREE'), orderChecks: true }],
    ),
  },
  {
    id: 'ind-mmkt',
    label: 'Individual - Premier Money Market',
    detail: 'Interest-bearing per the products.interestBearingCodes setting (if listed there).',
    needs: ['MMKT'],
    build: () => withProducts({}, [blankProduct('MMKT')]),
  },
  {
    id: 'minor-savmin',
    label: 'Minor - Minor Savings, minor owner + custodian',
    detail: 'The minor owns the account; the adult custodian is the signer.',
    needs: ['SAVMIN'],
    build: () => withProducts(
      { ownershipType: 'Minor', people: [{ role: 'primary', minor: true }, { role: 'custodian', minor: false }] },
      [blankProduct('SAVMIN')],
    ),
  },
  {
    id: 'minor-student',
    label: 'Minor - Minor My Student Checking, minor owner + custodian',
    detail: 'Overdraft coverage is not offered on minor accounts.',
    needs: ['STUDEN_MINOR'],
    build: () => withProducts(
      { ownershipType: 'Minor', people: [{ role: 'primary', minor: true }, { role: 'custodian', minor: false }] },
      [blankProduct('STUDEN_MINOR')],
    ),
  },
];

// ── Saved scenarios (this browser only) ──────────────────────────────────
const SAVED_KEY = 'rmp.packageGenerator.savedScenarios.v1';
const LAST_SUMMARY_KEY = 'rmp.packageGenerator.lastRunSummary.v1';

export interface SavedScenario {
  readonly name: string;
  readonly savedAt: string;
  readonly scenario: Scenario;
}

export function loadSavedScenarios(): SavedScenario[] {
  try {
    const raw = window.localStorage.getItem(SAVED_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as SavedScenario[]) : [];
  } catch {
    return [];
  }
}

export function storeSavedScenarios(list: SavedScenario[]): boolean {
  try {
    window.localStorage.setItem(SAVED_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

// ── Run comparison ───────────────────────────────────────────────────────
/** One document's fingerprint — what a run is compared on. No PDF bytes. */
export interface DocSummary {
  readonly key: string;
  readonly templateCode: string;
  readonly title: string;
  readonly accountId: string | null;
  readonly contentHash: string;
  readonly versionNumber: string;
  readonly versionStatus: string | null;
  readonly signerRoles: ReadonlyArray<string>;
  readonly pageCount: number;
  readonly requiresSignature: boolean;
}

export interface RunSummary {
  readonly runId: string;
  readonly generatedAtUtc: string;
  readonly versionSource: string;
  readonly docs: ReadonlyArray<DocSummary>;
}

export function docKey(d: Pick<PreviewDocument, 'templateCode' | 'accountId'>): string {
  return `${d.templateCode}|${d.accountId ?? ''}`;
}

export function summarize(run: PreviewRun): RunSummary {
  return {
    runId: run.runId,
    generatedAtUtc: run.generatedAtUtc,
    versionSource: run.versionSource,
    docs: run.documents.map((d) => ({
      key: docKey(d),
      templateCode: d.templateCode,
      title: d.title,
      accountId: d.accountId,
      contentHash: d.contentHash,
      versionNumber: d.versionNumber,
      versionStatus: d.versionStatus,
      signerRoles: d.signerRoles,
      pageCount: d.pageCount,
      requiresSignature: d.requiresSignature,
    })),
  };
}

export function loadLastSummary(): RunSummary | null {
  try {
    const raw = window.localStorage.getItem(LAST_SUMMARY_KEY);
    return raw ? (JSON.parse(raw) as RunSummary) : null;
  } catch {
    return null;
  }
}

export function storeLastSummary(s: RunSummary): void {
  try {
    window.localStorage.setItem(LAST_SUMMARY_KEY, JSON.stringify(s));
  } catch {
    // Browser storage unavailable - comparison still works within this visit.
  }
}

export type DiffKind = 'added' | 'removed' | 'changed' | 'same';

export interface DiffRow {
  readonly key: string;
  readonly kind: DiffKind;
  readonly before: DocSummary | null;
  readonly after: DocSummary | null;
  /** Plain-language list of what differs besides the content. */
  readonly changes: ReadonlyArray<string>;
}

export function diffRuns(before: RunSummary, after: RunSummary): DiffRow[] {
  const prev = new Map(before.docs.map((d) => [d.key, d]));
  const next = new Map(after.docs.map((d) => [d.key, d]));
  const keys = [...after.docs.map((d) => d.key), ...before.docs.map((d) => d.key).filter((k) => !next.has(k))];
  return keys.map((key) => {
    const b = prev.get(key) ?? null;
    const a = next.get(key) ?? null;
    if (!b) return { key, kind: 'added' as const, before: null, after: a, changes: [] };
    if (!a) return { key, kind: 'removed' as const, before: b, after: null, changes: [] };
    const changes: string[] = [];
    if (b.contentHash !== a.contentHash) changes.push('content');
    if (b.versionNumber !== a.versionNumber) changes.push(`version ${b.versionNumber} -> ${a.versionNumber}`);
    if (b.signerRoles.join(',') !== a.signerRoles.join(',')) changes.push(`signers ${b.signerRoles.join(', ') || 'none'} -> ${a.signerRoles.join(', ') || 'none'}`);
    if (b.pageCount !== a.pageCount) changes.push(`pages ${b.pageCount} -> ${a.pageCount}`);
    if (b.requiresSignature !== a.requiresSignature) changes.push(a.requiresSignature ? 'now needs a signature' : 'no longer needs a signature');
    return { key, kind: changes.length > 0 ? ('changed' as const) : ('same' as const), before: b, after: a, changes };
  });
}
